import { inject, Injectable } from "@angular/core";
import { ActionsSubject, Store } from '@ngrx/store';
import { combineLatest, Observable, of } from 'rxjs';
import {
  debounceTime,
  filter,
  first,
  map,
  switchMap,
  take,
  withLatestFrom,
} from 'rxjs/operators';
import * as PartnerProjectActions from './../../../../app/@store/projects/projects.actions';
import * as PartnerProjectSelectors from './../../../../app/@store/projects/projects.selectors';
import * as BreakoutActions from './../../../../app/@store/breakouts/breakouts.actions';
import * as BreakoutSelectors from './../../../../app/@store/breakouts/breakouts.selectors';
import * as VirtualSwitchActions from './../../../../app/@store/virtual_switches/virtual_switches.actions';
import { PartnerProject } from '../../../@shared/models/partner_project.model';
import * as _ from 'lodash';
import { OverlayAlertService } from '@gridscale/ingrid/components/overlay-alert';
import { NgrxService } from '../../../@shared/services/ngrx.service';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from '../../../@shared/services/toast.service';
import {
  BackgroundJobService,
  JOB_STATUS,
} from '@gridscale/ingrid/components/background-job';
import { ofType } from '@ngrx/effects';
import { Link } from '../../../@shared/models/link.model';
import { VirtualSwitch } from '../../../@shared/models/virtual_switch.model';
import { Breakout } from '../../../@shared/models/breakout.model';
import { SDN_DEFAULT_LOCATION_STORAGE_KEY } from './sdn.component';
import { getPartenrDefaultLocationUuid } from '../../../@store/partner/partner.selectors';
import { selectRouteParam } from '../../../@store/router/router.selector';
import {
  getLocationByUuid,
  isLocationByUuidLoaded,
  isLocationByUuidLoading,
} from '../../../@store/location/location.selectors';
import { loadLocationByUuid } from '../../../@store/location/location.actions';
import { LocalstorageService } from '@gridscale/gs-services/localstorage.service';
import { CreateVirtualSwitchComponent } from './virtual-switches/create-virtual-switch/create-virtual-switch.component';
import { toSignal } from '@angular/core/rxjs-interop';
import * as AccountSelectors from '../../../@store/account/account.selectors';
import { AccountService } from "../../../@shared/services/account.service";
import { CreateBreakoutComponent } from "./breakouts/create-breakout/create-breakout.component";

@Injectable({
  providedIn: 'root',
})
export class SDNService {
  readonly #store = inject(Store);
  readonly #accountsService = inject(AccountService);
  readonly #totalAccounts = toSignal(
    this.#store.select(AccountSelectors.getAccountsTotal),
    { initialValue: 0 }
  );

  constructor(
    private readonly overlayAlert: OverlayAlertService,
    private readonly ngrxService: NgrxService,
    private readonly translate: TranslateService,
    private readonly toastService: ToastService,
    private readonly backgroundJobService: BackgroundJobService,
    private readonly actions$: ActionsSubject,
    private readonly localStorage: LocalstorageService
  ) {}

  public showVirtualSwitchCreateOverlay() {
    if (this.#totalAccounts() === 0) {
      this.overlayAlert.confirm(
        this.translate.instant('SDN.VIRTUAL_SWITCH.OVERLAYS.NO_PROJECT.TITLE'),
        this.translate.instant('SDN.VIRTUAL_SWITCH.OVERLAYS.NO_PROJECT.TEXT'),
        [
          {
            label: this.translate.instant('cancel'),
            keyShortcut: 'Escape',
            mode: 'secondary',
            callback: (button, alert) => {
              alert.close();
              alert.callback.emit(false);
            }
          },
          {
            label: this.translate.instant('SDN.VIRTUAL_SWITCH.OVERLAYS.NO_PROJECT.BUTTON_OK'),
            keyShortcut: 'Enter',
            mode: 'primary',
            callback: (button, alert) => {
              alert.close();
              alert.callback.emit(true);
            }
          }
        ]
      ).subscribe(_confirm => {
        if (_confirm) {
          this.#accountsService.showCreateAccountOverlay();
        }
      });
    } else {
      const loc = this.localStorage.getItem(SDN_DEFAULT_LOCATION_STORAGE_KEY);

      this.overlayAlert.custom(
        {
          headline: this.translate.instant('sdn_create_switch'),
          iconCls: 'icon-a147_add_larger',
          buttons: [
            {
              label: this.translate.instant('cancel'),
              mode: 'secondary',
              keyShortcut: 'Escape',
              callback: (button, alert, childComponentRef) =>
                childComponentRef?.instance.cancel(),
            },
            {
              label: this.translate.instant('create'),
              mode: 'primary',
              disabled: true,
              callback: (button, alert, childComponentRef) =>
                childComponentRef?.instance.onSubmit(),
            },
          ],
          width: '680px',
          advancedAlignmentMode: true,
          data: {
            location_uuid: loc,
          },
          mandatoryLabel: true,
        },
        CreateVirtualSwitchComponent
      );
    }
  }

  public showBreakoutCreateOverlay() {
    if (this.#totalAccounts() === 0) {
      this.overlayAlert.confirm(
        this.translate.instant('SDN.BREAKOUT.OVERLAYS.NO_PROJECT.TITLE'),
        this.translate.instant('SDN.BREAKOUT.OVERLAYS.NO_PROJECT.TEXT'),
        [
          {
            label: this.translate.instant('cancel'),
            keyShortcut: 'Escape',
            mode: 'secondary',
            callback: (button, alert) => {
              alert.close();
              alert.callback.emit(false);
            }
          },
          {
            label: this.translate.instant('SDN.BREAKOUT.OVERLAYS.NO_PROJECT.BUTTON_OK'),
            keyShortcut: 'Enter',
            mode: 'primary',
            callback: (button, alert) => {
              alert.close();
              alert.callback.emit(true);
            }
          }
        ]
      ).subscribe(_confirm => {
        if (_confirm) {
          this.#accountsService.showCreateAccountOverlay();
        }
      });
    } else {
      const loc = this.localStorage.getItem(SDN_DEFAULT_LOCATION_STORAGE_KEY);

      this.overlayAlert.custom(
        {
          headline: this.translate.instant('sdn_create_breakout'),
          iconCls: 'icon-a147_add_larger',
          buttons: [
            {
              label: this.translate.instant('cancel'),
              mode: 'secondary',
              keyShortcut: 'Escape',
              callback: (button, alert, childComponentRef) =>
                childComponentRef?.instance.cancel(),
            },
            {
              label: this.translate.instant('create'),
              mode: 'primary',
              disabled: true,
              callback: (button, alert, childComponentRef) =>
                childComponentRef?.instance.onSubmit(),
            },
          ],
          width: '680px',
          advancedAlignmentMode: true,
          data: {
            location_uuid: loc,
          },
          mandatoryLabel: true,
        },
        CreateBreakoutComponent
      );
    }
  }

  /**
   * returns current location uuid or the default location
   */
  getLocationUuid$() {
    const locationUuid = this.localStorage.getItem(
      SDN_DEFAULT_LOCATION_STORAGE_KEY
    );
    if (locationUuid === null) {
      // get the default location
      return this.#store.select(getPartenrDefaultLocationUuid).pipe(
        first(),
        filter(
          (locationUuid) => locationUuid !== undefined && locationUuid !== ''
        )
      );
    } else {
      return of(locationUuid);
    }
  }

  /**
   * returns the right filter for sdn endpoints contrained to a location, dependent of if we are in SDN or Accound-SDN view
   */
  getSdnFilter$() {
    return this.#store.select(selectRouteParam('account_uuid')).pipe(
      take(1),
      switchMap((accountUuid) => {
        if (accountUuid === undefined) {
          return this.getLocationUuid$();
        } else {
          return of(undefined);
        }
      }),
      switchMap((locationUuid) => {
        if (locationUuid === undefined) {
          return of(undefined);
        }

        // load location and return hybrid core uuid
        this.#store
          .select(isLocationByUuidLoaded({ location_uuid: locationUuid }))
          .pipe(
            withLatestFrom(
              this.#store.select(
                isLocationByUuidLoading({ location_uuid: locationUuid })
              )
            ),
            take(1)
          )
          .subscribe(([loaded, loading]) => {
            if (!loaded && !loading) {
              this.#store.dispatch(
                loadLocationByUuid({ location_uuid: locationUuid })
              );
            }
          });

        return this.#store
          .select(getLocationByUuid({ location_uuid: locationUuid }))
          .pipe(
            filter((location) => location !== undefined),
            take(1),
            map((location) => location!.hybrid_core_uuid)
          );
      }),
      map((hybridcoreUuid) => {
        if (hybridcoreUuid !== undefined) {
          return ['hybrid_core_uuid=' + hybridcoreUuid];
        }
        return [];
      })
    );
  }

  /**
   * returns the right filter for breakout endpoints (they work with location_uuid instead of hybrid_core_uuid) contrained to a location, dependent of if we are in SDN or Accound-SDN view
   */
  getSdnFilterBreakout$() {
    return this.#store.select(selectRouteParam('account_uuid')).pipe(
      take(1),
      switchMap((accountUuid) => {
        if (accountUuid === undefined) {
          // get the location from localstorage or use the partners default locatoin
          const locationUuid = this.localStorage.getItem(
            SDN_DEFAULT_LOCATION_STORAGE_KEY
          );
          if (locationUuid === null) {
            // get the default location
            return this.#store.select(getPartenrDefaultLocationUuid).pipe(
              first(),
              filter(
                (locationUuid) =>
                  locationUuid !== undefined && locationUuid !== ''
              ),
              map(
                (locationUuid) =>
                  [accountUuid, locationUuid] as [string, string]
              )
            );
          } else {
            return of([accountUuid, locationUuid]);
          }
        } else {
          return of([accountUuid, undefined]);
        }
      }),
      map(([accountUuid, locationUuid]) => {
        if (locationUuid !== undefined) {
          return 'location_uuid=' + locationUuid;
        }
        if (accountUuid !== undefined) {
          return 'contract_uuid=' + accountUuid;
        }
        return '';
      })
    );
  }

  /**
   * loads "all" projects for a locatoin into the store, resolves to true when done
   * This will load all projects for the given active location (within the SDN Module)
   */
  loadAllProjectsForLocation$(locationUuid: string): Observable<boolean> {
    return this.#store
      .select(
        PartnerProjectSelectors.isPartnerProjectsForLocationListLoading({
          locationUuid,
        })
      )
      .pipe(
        debounceTime(100),
        withLatestFrom(
          this.#store.select(
            PartnerProjectSelectors.isPartnerProjectsForLocationListLoaded({
              locationUuid,
            })
          ),
          this.#store.select(
            PartnerProjectSelectors.isPartnerProjectsForLocationListLoadingNext(
              { locationUuid }
            )
          ),
          this.#store.select(
            PartnerProjectSelectors.hasProjectsForLocationNext({ locationUuid })
          ),
          this.#store.select(
            PartnerProjectSelectors.hasProjectsForLocationError({
              locationUuid,
            })
          )
        ),
        map(([loading, loaded, loadingNext, hasNext, hasError]) => {
          if (!loaded && !loading && !loadingNext) {
            this.#store.dispatch(
              PartnerProjectActions.loadPartnerProjectsForLocation({
                locationUuid,
              })
            );
          }

          if (!loadingNext && hasNext && !hasError) {
            this.#store.dispatch(
              PartnerProjectActions.loadPartnerProjectsForLocationNext({
                locationUuid,
              })
            );
          }

          if (loaded && !loading && !loadingNext && !hasNext && !hasError) {
            return true;
          }

          return false;
        }),
        filter((done) => !!done)
      );
  }

  /**
   * loads "all" projects into the store, resolves to true when done
   * This will load all projects for the active location (within the SDN Module)
   * or all projects of the current account (SDN within account details)
   */
  loadAllProjects$(): Observable<boolean> {
    this.getSdnFilterBreakout$().subscribe((filter) => {
      this.#store.dispatch(
        PartnerProjectActions.setListConfig({
          config: {
            filter,
          },
        })
      );
    });

    return this.#store
      .select(PartnerProjectSelectors.isPartnerProjectsListLoading)
      .pipe(
        debounceTime(100),
        withLatestFrom(
          this.#store.select(
            PartnerProjectSelectors.isPartnerProjectsListLoaded
          ),
          this.#store.select(
            PartnerProjectSelectors.isPartnerProjectsListLoadingNext
          ),
          this.#store.select(PartnerProjectSelectors.hasProjectsNext),
          this.#store.select(PartnerProjectSelectors.hasProjectsError)
        ),
        map(([loading, loaded, loadingNext, hasNext, hasError]) => {
          if (!loaded && !loading && !loadingNext) {
            this.#store.dispatch(PartnerProjectActions.loadPartnerProjects());
          }

          if (!loadingNext && hasNext && !hasError) {
            this.#store.dispatch(
              PartnerProjectActions.loadPartnerProjectsNext()
            );
          }

          if (loaded && !loading && !loadingNext && !hasNext && !hasError) {
            return true;
          }

          return false;
        }),
        filter((done) => !!done)
      );
  }

  /**
   * finds the project in the project list without needing the contract uuid
   * @param project_uuid
   * @returns
   */
  getProject$(project_uuid: string): Observable<PartnerProject> {
    return this.#store.select(PartnerProjectSelectors.getProjects).pipe(
      withLatestFrom(
        this.#store.select(PartnerProjectSelectors.isPartnerProjectsListLoaded),
        this.#store.select(
          PartnerProjectSelectors.isPartnerProjectsListLoadingNext
        ),
        this.#store.select(PartnerProjectSelectors.hasProjectsNext),
        this.#store.select(PartnerProjectSelectors.hasProjectsError)
      ),
      filter(
        ([projects, loaded, loadingNext, hasNext, hasError]) =>
          projects !== undefined &&
          !!loaded &&
          !loadingNext &&
          !hasNext &&
          !hasError
      ),
      take(1),
      map(([projects, loaded, loadingNext, hasNext]) => {
        return _.find(projects, { object_uuid: project_uuid });
      }),
      filter((project) => project !== undefined),
      map((project) => project!)
    );
  }

  /**
   * deletes a breakout with confirmation alert
   */
  deleteBreakout$(breakout_uuid: string): Observable<boolean> {
    return new Observable((obs) => {
      this.#store
        .select(BreakoutSelectors.isBreakoutLoaded({ breakout_uuid }))
        .pipe(
          withLatestFrom(
            this.#store.select(
              BreakoutSelectors.isBreakoutLoading({ breakout_uuid })
            )
          ),
          take(1)
        )
        .subscribe(([loaded, loading]) => {
          if (!loaded && !loading) {
            this.#store.dispatch(
              BreakoutActions.loadBreakout({ breakout_uuid })
            );
          }
        });

      this.#store
        .select(
          BreakoutSelectors.isBreakoutVirtualSwitchesLoaded({ breakout_uuid })
        )
        .pipe(
          withLatestFrom(
            this.#store.select(
              BreakoutSelectors.isBreakoutVirtualSwitchesLoading({
                breakout_uuid,
              })
            )
          ),
          take(1)
        )
        .subscribe(([loaded, loading]) => {
          if (!loaded && !loading) {
            this.#store.dispatch(
              BreakoutActions.loadBreakoutVirtualSwitches({ breakout_uuid })
            );
          }
        });

      combineLatest([
        this.#store.select(BreakoutSelectors.getBreakout({ breakout_uuid })),
        this.#store.select(
          BreakoutSelectors.getBreakoutVirtualSwitches({ breakout_uuid })
        ),
        this.#store.select(
          BreakoutSelectors.isBreakoutVirtualSwitchesLoaded({ breakout_uuid })
        ),
      ])
        .pipe(
          filter(
            ([breakout, virtualSwitches, virtualSwitchesLoaded]) =>
              breakout !== undefined && !!virtualSwitchesLoaded
          ),
          take(1)
        )
        .subscribe(([breakout, virtualSwitches, virtualSwitchesLoaded]) => {
          this.overlayAlert
            .confirmDelete({
              objectName: breakout!.name!,
              objectType: 'Breakout',
              cannotBeUndone: true,
              typeToConfirm: breakout!.name!,
              subobjectLists: {
                switches: {
                  intro: this.translate.instant(
                    'sdn_breakout_delete_switches_will_be_unrelated'
                  ),
                  listHeadlines: ['Virtual Switch'],
                  objects: _.keyBy(
                    _.map(virtualSwitches, (breakoutSwitch) => ({
                      selected: true,
                      disabled: true,
                      object_uuid: breakoutSwitch.object_uuid!,
                      dataColumns: [breakoutSwitch.virtual_switch_name!],
                    })),
                    (r) => r.object_uuid!
                  ),
                },
              },
            })
            .subscribe((result) => {
              if (result.confirmed) {
                const doBreakoutDelete = () => {
                  this.ngrxService.callBasicActionWithCallbacks({
                    actionToCall: BreakoutActions.deleteBreakout({
                      breakout_uuid,
                    }),
                    failureAction: BreakoutActions.deleteBreakoutFailure,
                    successAction: BreakoutActions.deleteBreakoutSuccess,
                    failureCallback: () => {
                      obs.next(false);
                      obs.complete();
                    },
                    successCallback: () => {
                      obs.next(true);
                      obs.complete();

                      this.toastService.add({
                        severity: 'success',
                        summary: this.translate.instant(
                          'sdn_breakout_delete_success'
                        ),
                      });
                    },
                  });
                };

                if (result.subobjectsSelected?.switches?.length > 0) {
                  // dispatch delete on each switch connection, and wait for everyones success action

                  const backgroundJob = this.backgroundJobService.addJob({
                    status: JOB_STATUS.RUNNING,
                    text: this.translate.instant('sdn_deleting_breakout'),
                    percentage: 0,
                  });

                  let requests = result.subobjectsSelected.switches.length;
                  this.actions$
                    .pipe(
                      ofType(
                        BreakoutActions.deleteBreakoutVirtualSwitchSuccess,
                        BreakoutActions.deleteBreakoutVirtualSwitchFailure
                      ),
                      take(result.subobjectsSelected.switches.length)
                    )
                    .subscribe((action) => {
                      --requests;

                      if (
                        action.type ===
                        BreakoutActions.deleteBreakoutVirtualSwitchSuccess.type
                      ) {
                        const percentage = Math.round(
                          ((result.subobjectsSelected.switches.length -
                            requests) /
                            result.subobjectsSelected.switches.length) *
                            100
                        );

                        if (backgroundJob) {
                          this.backgroundJobService.updateJob(backgroundJob, {
                            percentage,
                          });
                          if (percentage === 100) {
                            this.backgroundJobService.updateJob(backgroundJob, {
                              status: JOB_STATUS.SUCCESS,
                            });
                          }
                        }

                        if (requests === 0) {
                          doBreakoutDelete();
                        }
                      } else {
                        // failure
                        if (backgroundJob) {
                          this.backgroundJobService.updateJob(backgroundJob, {
                            status: JOB_STATUS.FAILED,
                            subtext: (
                              action as ReturnType<
                                typeof BreakoutActions.deleteBreakoutVirtualSwitchFailure
                              >
                            ).error?.message,
                          });
                        }

                        obs.next(false);
                        obs.complete();
                      }
                    });

                  _.forEach(
                    result.subobjectsSelected.switches,
                    (switchUuid) => {
                      this.#store.dispatch(
                        BreakoutActions.deleteBreakoutVirtualSwitch({
                          breakout_uuid: breakout!.object_uuid!,
                          virtual_switch_uuid: switchUuid,
                        })
                      );
                    }
                  );
                } else {
                  doBreakoutDelete();
                }
              }
            });
        });
    });
  }

  removeBreakout(
    breakout: Partial<Breakout>,
    virtualSwitch: Pick<VirtualSwitch, 'name' | 'object_uuid'>
  ) {
    this.overlayAlert
      .confirmDelete(
        {
          objectName: breakout.name!,
          objectType: 'Breakout',
          customHeadline: this.translate.instant(
            'sdn_remove_breakout_from_switch_headline'
          ),
          message: this.translate.instant(
            'sdn_remove_breakout_from_switch_message',
            {
              breakoutName: breakout.name,
              switchName: virtualSwitch.name,
            }
          ),
          customHeadlineIconCls: 'icon-a161_disconnect',
        },
        [
          {
            label: this.translate.instant('cancel'),
            mode: 'secondary',
            keyShortcut: 'Escape',
            callback: (button, alert) => {
              alert.callback.emit(false);
              alert.close();
            },
          },
          {
            label: this.translate.instant('sdn_unrelate_project'),
            mode: 'danger',
            callback: (button, alert) => {
              alert.callback.emit(true);
              alert.close();
            },
          },
        ]
      )
      .subscribe((result) => {
        if (result.confirmed) {
          this.ngrxService.callBasicActionWithCallbacks({
            actionToCall: BreakoutActions.deleteBreakoutVirtualSwitch({
              breakout_uuid: breakout.object_uuid!,
              virtual_switch_uuid: virtualSwitch.object_uuid,
            }),
            successAction: BreakoutActions.deleteBreakoutVirtualSwitchSuccess,
            failureAction: BreakoutActions.deleteBreakoutVirtualSwitchFailure,
            successCallback: () => {
              this.toastService.add({
                severity: 'success',
                summary: this.translate.instant(
                  'sdn_unrelate_breakout_success'
                ),
              });
            },
            failureCallback: () => {},
          });
        }
      });
  }

  removeLink(
    link: Partial<Link>,
    virtualSwitch: Partial<VirtualSwitch>,
    onDone = () => {}
  ) {
    this.overlayAlert
      .confirmDelete(
        {
          objectName: link.name!,
          objectType: 'Link',
          customHeadline: this.translate.instant('disconnect_objects'),
          message: this.translate.instant('sdn_remove_link_question', {
            linkName: link.name,
            switchName: virtualSwitch.name,
          }),
        },
        [
          {
            label: this.translate.instant('cancel'),
            mode: 'secondary',
            keyShortcut: 'Escape',
            callback: (button, alert) => {
              alert.callback.emit(false);
              alert.close();
            },
          },
          {
            label: this.translate.instant('disconnect_objects_yes'),
            mode: 'danger',
            callback: (button, alert) => {
              alert.callback.emit(true);
              alert.close();
            },
          },
        ]
      )
      .subscribe((result) => {
        if (result.confirmed) {
          this.ngrxService.callBasicActionWithCallbacks({
            actionToCall: VirtualSwitchActions.patchVirtualSwitch({
              virtual_switch_uuid: virtualSwitch.object_uuid!,
              data: {
                link_uuid: null,
              },
            }),
            successAction: VirtualSwitchActions.patchVirtualSwitchSuccess,
            failureAction: VirtualSwitchActions.patchVirtualSwitchFailure,
            successCallback: () => {
              this.toastService.add({
                severity: 'success',
                summary: this.translate.instant('sdn_remove_link_success'),
              });
              onDone();
            },
            failureCallback: () => {},
          });
        }
      });
  }
}
